/**
 * Sayfa bileşenleri için `React.lazy` + önceden yükleme (owner 2026-10-04).
 *
 * Sorun: React Router 7 her gezinmeyi bir "geçiş" (startTransition) içinde
 * yapar. Yeni sayfanın JS dosyası henüz inmemişse React eski sayfayı ekranda
 * tutar; adres değişir ama ekran değişmez. Yavaş ağda (DEV'de "Slow 3G" ile
 * ölçüldü) bu 5 saniye sürdü ve "kenar çubuğuna tıkladım, gitmedi" gibi
 * görünüyordu.
 *
 * Çözüm: her sayfa yükleyicisi bir listeye kaydolur; uygulama açıldıktan
 * sonra tarayıcı boştayken (`preloadAllPages`) hepsi sırayla indirilir.
 * Sonraki gezinmeler dosyayı beklemez. Kenar çubuğundaki ilerleme çubuğu
 * (`NavigationProgress`) ilk açılıştaki beklemeyi görünür kılar.
 *
 * Kapsam (2026-10-05): bir sayfa dosyası inince CSS'i de sayfaya eklenir ve
 * global kalır. Admin sayfalarının CSS'i aynı sınıf adlarını (örn.
 * `.rfq-manual-products-dashboard`, `.quotes-expanded-products`) farklı
 * değerlerle tanımlıyor; müşteriye de inince müşteri sayfalarının düzenini
 * eziyordu (DEV'de ölçüldü: Özet tablosunda `min-height` admin değerine
 * döndü). Bu yüzden her yükleyici bir kapsam taşır ve yalnız o rolün
 * açabileceği sayfalar önceden indirilir. Kapsamsız = iki rol de açar.
 */
import React from 'react';

type Loader<T> = () => Promise<{ default: T }>;

export type PreloadRole = 'admin' | 'user';

const loaders: Array<{ load: () => Promise<unknown>; only?: PreloadRole }> =
  [];

export function lazyWithPreload<T extends React.ComponentType<any>>(
  loader: Loader<T>,
  only?: PreloadRole
): React.LazyExoticComponent<T> {
  loaders.push({ load: loader, only });
  return React.lazy(loader);
}

let started = false;

/** Rolün açabileceği sayfa dosyalarını sırayla, boşta indirir. Bir kez çalışır. */
export function preloadAllPages(role: PreloadRole): void {
  if (started) return;
  started = true;
  const idle = (cb: () => void) => {
    const ric = (window as any).requestIdleCallback as
      | ((fn: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (ric) ric(cb, { timeout: 3000 });
    else window.setTimeout(cb, 300);
  };
  const queue = loaders
    .filter((l) => !l.only || l.only === role)
    .map((l) => l.load);
  const next = () => {
    const load = queue.shift();
    if (!load) return;
    // Hata sessizce geçilir: dosya gerçekten açılırken yine denenir.
    load()
      .catch(() => undefined)
      .finally(() => idle(next));
  };
  idle(next);
}
